import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  autoOcrOptions,
  buttonsStaticConfig, ColumnTableItem,
  fileUploadModeOptions, FileUploadModes,
  GeneralSandbox,
  InvoiceDocument,
  NavigationService,
  NotificationEnum,
  NotificationService,
  RoutesEnum
} from '../../../global';
import {catchError, filter, takeUntil, tap} from 'rxjs/operators';
import {BehaviorSubject, Observable, of, Subject, throwError} from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { HttpStatusCode } from '@angular/common/http';
import {UploadDocumentSandbox} from '../../../invoices/sandboxes/upload-document-sandbox.service';
import { ocrIcons } from '../../../invoices/functions/documents.functions';
import { DialogContainerComponent } from '../../components/dialog-container/dialog-container.component';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ToggleComponent } from '../../components/inputs/toggle/toggle.component';
import { MessageComponent } from '../../components/message/message.component';
import { SelectInputComponent } from '../../components/inputs/select-input/select-input.component';
import { UserDivisionSelectComponent } from '../../components/user-division-select/user-division-select.component';
import { DocumentItemComponent } from '../../components/document-item/document-item.component';
import { DragAndDropComponent } from '../../components/drag-and-drop/drag-and-drop.component';

interface DocumentUploadDialogConfig {
  title?: string;
  save?: (documentIds: Array<number>) => Observable<any>;
  fileUploading: (file: File, uploadMode: FileUploadModes, filename: string, userDivisionId?: number) => Observable<any>;
  documentDelete: (documentIds: Array<number | string>) => Observable<any>;
  disabledClose?: boolean;
  attachToExists?: boolean;
  hideUploadOptions?: boolean;
  acceptedFileMimeTypes?: string;
  hideAllOpenIcon?: boolean;
  hideAutoOcr?: boolean;
}

@Component({
    selector: 'slm-dialog-upload-dialog',
    templateUrl: './document-upload-dialog.component.html',
    imports: [DialogContainerComponent, NgIf, ToggleComponent, MessageComponent, SelectInputComponent, UserDivisionSelectComponent, NgFor, DocumentItemComponent, DragAndDropComponent, AsyncPipe, TranslatePipe]
})
export class DocumentUploadDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject<MatDialogRef<DocumentUploadDialogComponent>>(MatDialogRef);
  readonly notificationService = inject(NotificationService);
  private readonly router = inject(NavigationService);
  data = inject<DocumentUploadDialogConfig>(MAT_DIALOG_DATA);
  private readonly generalSandbox = inject(GeneralSandbox);
  private readonly translationService = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly uploadSandbox = inject(UploadDocumentSandbox);

  private readonly destroy$ = new Subject();
  private multiUpload = false;
  private multiUploadIndex = -1;
  private multiUploadFileIndexes: Array<number> = [];

  public dialogButtons = [buttonsStaticConfig.close];
  public documents: Array<InvoiceDocument> = [];
  public documentsSignal = signal<Array<InvoiceDocument>>([]);
  public fileUploadModeOptions = fileUploadModeOptions;
  public autoOcrOptions = autoOcrOptions;
  private filesToAutoOcr$ = new BehaviorSubject<Array<number>>([]);

  public fileUploadFormGroup: FormGroup<{
    uploadMode: FormControl<FileUploadModes>,
    userDivisionId: FormControl<number>,
    autoOcr: FormControl<boolean>
  }>;

  public maxFileSizeMB = 8;
  public readonly defaultMimeTypes = 'application/pdf,image/png,image/jpeg,image/jpg';

  public useDivisions$: Observable<boolean>;

  public tableConfig: Array<ColumnTableItem> = [
    {
      value: 'LBL_UPLOAD_FILL.LIST.FILE_NAME',
      key: 'name',
      type: 'text'
    },
    {
      value: 'LBL_UPLOAD_FILL.DOCUMENT_OCR',
      key: 'ocrIcon',
      type: 'icon'
    }
  ];

  ngOnInit() {
    this.fileUploadFormGroup = this.fb.group({
      uploadMode: [FileUploadModes.AS_SEPARATE_PAGES, [Validators.required]],
      userDivisionId: [{ value: null, disabled: true }],
      autoOcr: [false, [Validators.required]]
    });

    this.dialogRef.disableClose = this.data.disabledClose;
    if (this.data.save && !this.data.attachToExists) {
      this.dialogButtons = [...this.dialogButtons, buttonsStaticConfig.save];
    }

    if(!this.data.acceptedFileMimeTypes){
      this.data.acceptedFileMimeTypes = this.defaultMimeTypes;
    }

    this.generalSandbox.fileUploadMode$.pipe(
      tap(value => this.fileUploadFormGroup.get('uploadMode').setValue(value)),
      takeUntil(this.destroy$)
    ).subscribe();

    const autoOcrControl = this.fileUploadFormGroup.get('autoOcr');
    this.generalSandbox.autoOcrOnUpload$.pipe(
      tap(value => autoOcrControl.setValue(this.data.hideAutoOcr ? false : value)),
      takeUntil(this.destroy$)
    ).subscribe();

    if(this.data.hideAutoOcr){
      this.fileUploadFormGroup.get('autoOcr').disable();
    }

    this.filesToAutoOcr$.pipe(
      filter(values => (values || []).length > 0),
      tap((values: Array<number>) => {
        this.startOcr(values[0]);
        const filesToAutoOcr = this.filesToAutoOcr$.getValue();
        this.filesToAutoOcr$.next(filesToAutoOcr.filter((file) => file !== values[0]));
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.useDivisions$ = this.generalSandbox.useDivision$;
  }

  unauthorizedFileFormat(event) {
    this.notificationService.notify(
      this.translationService.instant(event.message, event.error === 'size' ? {maxSize: this.maxFileSizeMB} : {}),
      NotificationEnum.ERROR
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public documentEvent(index: number, event: string): void {
    if (this.multiUpload) {
      this.notificationService.notify('MESSAGE.PLEASE_WAIT_FOR_THE_UPLOAD', NotificationEnum.WARNING);
      return;
    }
    switch (event) {
      case 'refresh':
        this.reUploadFile(index);
        break;
      case 'cancel':
        this.cancelUpload(index);
        break;
      case 'remove':
        this.deleteDocument(index);
        break;
      case 'open':
        this.documentsSignal.update((value) => {
          value[index].hideOpen = true;
          return value;
        });
        this.router.openInNewTab([RoutesEnum.INVOICES, RoutesEnum.DOCUMENTS_FILLING_FORM], {
          id: this.documentsSignal()[index].ids,
          selectedId: this.documentsSignal()[index].ids
        });
    }
  }

  selectedFile(selectedFiles: Array<File>): void {
    if (!selectedFiles || !selectedFiles?.length) {
      return;
    }
    if (this.multiUploadIndex !== -1) {
      this.notificationService.notify('MESSAGE.PLEASE_WAIT_FOR_THE_UPLOAD', NotificationEnum.WARNING);
      return;
    }
    const files = (selectedFiles ?? []).filter(file => !!file);
    this.multiUpload = !!files?.length;
    this.dialogRef.disableClose = true;
    this.multiUploadFileIndexes.length = 0;
    (files || []).forEach((file) => {
      let index;

      this.documentsSignal.update((value) => {
        index = value.push({
          ids: [],
          name: file.name,
          file,
          progress: 0,
          status: 'uploading',
          subscription: null
        });
        return value;
      });

      if (this.multiUpload) {
        this.multiUploadFileIndexes.push(index - 1);
      } else {
        this.uploadFile(index - 1, file);
      }
    });
    if (this.multiUpload && this.multiUploadFileIndexes.length) {
      this.multiUploadIndex = 0;
      const index = this.multiUploadFileIndexes[this.multiUploadIndex];
      this.uploadFile(index, this.documentsSignal()[index].file);
    }
  }

  private goToNextMultiFile() {
    if (this.multiUploadIndex === -1) {
      return;
    }
    if (this.multiUploadIndex < this.multiUploadFileIndexes.length - 1) {
      ++this.multiUploadIndex;
      const index = this.multiUploadFileIndexes[this.multiUploadIndex];
      setTimeout(() => {
        this.uploadFile(index, this.documentsSignal()[index].file);
      }, 0);
    } else {
      this.multiUploadIndex = -1;
      this.multiUpload = false;
      this.enableClose();
    }
  }

  private uploadFile(index: number, file: File): void {

    this.documentsSignal.update(value => {
      value[index].subscription = this.data
        .fileUploading(
          file,
          this.fileUploadFormGroup.get('uploadMode').value,
          this.documentsSignal()[index]?.name,
          this.fileUploadFormGroup.get('userDivisionId').value
        )
        .pipe(
          catchError((err) => {
            if (+err?.status === HttpStatusCode.PayloadTooLarge) {
              const name = err?.error?.name || '';

              this.documentsSignal.update(value => {
                value[index].status = 'timeout';
                return value;
              });

              this.dialogRef.disableClose = false;
              if (name) {
                this.documentsSignal.update(value => {
                  value[index].name = name;
                  return value;
                });
              }
            }
            this.unsuccessfulUpload(index, null);
            this.goToNextMultiFile();
            return throwError(err);
          })
        )
        .subscribe((response: any) => {
          switch (response.type) {
            case 'progress':
              this.setProgress(index, response.result);
              break;
            case 'response':
              this.finishUpload(index, response.result);
              if (this.multiUpload) {
                this.goToNextMultiFile();
              }
              break;
          }
        });
      return value;
    });
  }

  enableClose() {
    const hasUpload = this.documentsSignal().find(document => document.status === 'uploading');
    if (!hasUpload && !this.data.save) {
      this.dialogRef.disableClose = false;
    }
  }

  private cancelUpload(index: number): void {
    if (this.isValid(index)) {
      if (!!this.documentsSignal()[index].subscription) {
        this.documentsSignal()[index].subscription.unsubscribe();
      }
      this.documentsSignal.update(value => {
        value[index].status = 'canceled'
        return value;
      });
      if (!this.multiUpload) {
        this.enableClose();
      }
    }
  }

  private reUploadFile(index: number): void {
    if (this.isValid(index, 'canceled') || this.isValid(index, 'error') || this.isValid(index, 'timeout')) {
      this.documentsSignal.update(value => {
        value[index].progress = 0;
        value[index].status = 'uploading';
        return value;
      });

      this.dialogRef.disableClose = true;
      this.uploadFile(index, this.documentsSignal()[index].file);
    }
  }

  private deleteDocument(index: number): void {
    if (this.isValid(index, 'done')) {
      const ids = this.documentsSignal()[index]?.ids.length > 0 ? this.documentsSignal()[index]?.ids : [this.documentsSignal()[index]?.data?.id];
      this.data.documentDelete(ids || []).subscribe();
      this.documentsSignal.update(value => {
        value.splice(index, 1);
        return value;
      });
    }
  }

  private isValid(index: number, status: string = 'uploading'): boolean {
    return (
      index < this.documentsSignal().length &&
      index >= 0 &&
      this.documentsSignal()[index].status === status
    );
  }

  private setProgress(index: number, progress: number): void {
    if (this.isValid(index)) {
      this.documentsSignal.update(value => {
        value[index].progress = progress;
        return value;
      });
    }
  }

  private finishUpload(
    index: number,
    response: { items: Array<number>; name: string }
  ): void {
    if (this.isValid(index)) {
      const autoOcrControl = this.fileUploadFormGroup.get('autoOcr');
      const uploadModeControl = this.fileUploadFormGroup.get('uploadMode');

      this.documentsSignal.update(value => {

        if(uploadModeControl.value === FileUploadModes.AS_FILE || this.data?.hideUploadOptions) {
          value[index].ids = response?.items || [];
          value[index].name = response?.name;
          value[index].progress = 100;
          value[index].status = 'done';
          value[index].data = response;
          value[index].ocrStatus = !!autoOcrControl.value ? -1 : 0;

          return value;
        }else{
          const newDocuments: Array<InvoiceDocument> = (response?.items || []).map((id, pageNumber) => {
            return {
              ids: [id],
              name: this.translationService.instant('INVOICE.INVOICE_UPLOAD_PAGES_LABEL', { filename: response?.name, page: pageNumber+1 }),
              file: value[index].file,
              progress: 100,
              status: 'done',
              data: response,
              subscription: value[index].subscription,
              ocrStatus: !!autoOcrControl.value ? -1 : 0
            };
          });
          value.push(...newDocuments);
          value.splice(index, 1, {name: null});
          return value;
        }
      });

      if (!!autoOcrControl.value) {
        if(uploadModeControl.value === FileUploadModes.AS_FILE){
          this.filesToAutoOcr$.next([...this.filesToAutoOcr$.getValue(), response?.items[0]]);
        }else{
          (response?.items || []).map(id => {
            this.filesToAutoOcr$.next([...this.filesToAutoOcr$.getValue(), id]);
          });
        }
      }

      if (this.data.save) {
        this.documentsSignal.update(value => {
          value[index].hideOpen = true;
          value[index].hideClose = true;
          return value;
        });

        if (this.data.attachToExists) {
          this.saveFileUpload();
        }
      }
      if (!this.multiUpload) {
        this.enableClose();
      }
    }
  }

  private unsuccessfulUpload(index: number, name: string): void {
    if (this.isValid(index)) {
      this.documentsSignal.update(value => {
        value[index].status = 'error';
        return value;
      });
      if (!this.multiUpload) {
        this.enableClose();
      }
      if (!!name) {
        this.documentsSignal.update(value => {
          value[index].name = name;
          return value;
        });
      }
    }
  }

  public closeDialog(event): void {
    if (this.dialogRef.disableClose && !this.data.save) {
      this.notificationService.notify('MESSAGE.FILE_UPLOAD_IN_PROGRESS', NotificationEnum.WARNING);
      return;
    }

    switch (event) {
      case 'save':
        this.saveFileUpload();
        break;
      case 'close':
        const documents = this.createDocumentsObj();
        if (!!documents.length && !this.data.save) {
          this.dialogRef.close(documents);
        } else if(this.data.save && this.data.attachToExists){
          this.dialogRef.close(documents);
        }else{
          this.dialogRef.close();
        }
        break;
    }
  }

  private changeSaveButtonLoadingState(isLoading: boolean){
    if(!this.data.attachToExists){
      this.dialogButtons[1].isLoading = isLoading;
    }
  }

  private createDocumentsObj(){
    return this.documentsSignal()
      .filter((document) => document.status === 'done')
      .map((document) => ({
        name: document.file.name,
        size: document.file.size,
        ids: document.ids,
        data: document?.data
      }));
  }

  private saveFileUpload(){
    if (this.dialogButtons[1]?.isLoading) {
      return;
    }

    const documents = this.createDocumentsObj();

    if (!documents.length) {
      this.notificationService.notify('INVOICE.NO_SELECTED_FILE', NotificationEnum.WARNING);
      return;
    }

    this.changeSaveButtonLoadingState(true);
    if(!this.data.attachToExists){
      this.dialogButtons[1].isLoading = true;
    }
    this.data.save(
      documents.reduce((previousValue, currentValue) => [...previousValue, ...currentValue.ids], [])
    ).subscribe({
        next: () => {
          if(!this.data.attachToExists){
            this.changeSaveButtonLoadingState(false);
            this.dialogRef.close(documents);
          }
        },
        error: () => {
          this.changeSaveButtonLoadingState(false);
        }
      }
    );
  }

  private startOcr(id) {
    this.uploadSandbox.startOcr(id).pipe(
      tap((response) => {
        this.documentsSignal.update(docs => {
          const doc = docs.find(file => file?.ids?.includes(id));
          if (doc) {
            doc.ocrStatus = response.status;
            doc.ocrIcon = ocrIcons(response.status);
          }
          return docs;
        });
      }),
      catchError(() => of({ status: 0 })),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  public trackById = (_, item: InvoiceDocument) => item?.ids;
}
